import { Ban, Clock3, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SessionWhatsAppReceiptLevel } from '@/utils/sessionWhatsAppDisplay';

type WhatsAppMessageReceiptProps = {
	level: SessionWhatsAppReceiptLevel;
	className?: string;
	size?: 'sm' | 'md';
};

const WhatsAppSingleCheck = ({ className }: { className?: string }) => (
	<svg
		viewBox="0 0 12 11"
		width="12"
		height="11"
		className={cn('inline-block shrink-0', className)}
		aria-hidden
	>
		<path
			fill="currentColor"
			d="M11.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-2.405-2.272a.463.463 0 0 0-.336-.146.47.47 0 0 0-.343.15.448.448 0 0 0-.14.336.43.43 0 0 0 .15.332l2.798 2.643a.46.46 0 0 0 .308.111.48.48 0 0 0 .374-.178l6.526-8.066a.447.447 0 0 0 .097-.375.43.43 0 0 0-.156-.216z"
		/>
	</svg>
);

const WhatsAppDoubleCheck = ({ className }: { className?: string }) => (
	<svg
		viewBox="0 0 16 11"
		width="16"
		height="11"
		className={cn('inline-block shrink-0', className)}
		aria-hidden
	>
		<path
			fill="currentColor"
			d="M11.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-2.405-2.272a.463.463 0 0 0-.336-.146.47.47 0 0 0-.343.15.448.448 0 0 0-.14.336.43.43 0 0 0 .15.332l2.798 2.643a.46.46 0 0 0 .308.111.48.48 0 0 0 .374-.178l6.526-8.066a.447.447 0 0 0 .097-.375.43.43 0 0 0-.156-.216z"
		/>
		<path
			fill="currentColor"
			d="M15.266.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-1.405-1.328-1.005.957 1.68 1.59a.46.46 0 0 0 .308.111.48.48 0 0 0 .374-.178l6.526-8.066a.447.447 0 0 0 .097-.375.43.43 0 0 0-.156-.216z"
		/>
	</svg>
);

const RECEIPT_CLASS: Record<
	Exclude<SessionWhatsAppReceiptLevel, 'pending' | 'failed' | 'cancelled'>,
	string
> = {
	sent: 'text-stone-400',
	delivered: 'text-stone-400',
	read: 'text-sky-500',
};

const WhatsAppMessageReceipt = ({
	level,
	className,
	size = 'sm',
}: WhatsAppMessageReceiptProps) => {
	const scale = size === 'md' ? 'scale-110' : undefined;
	const clockSize = size === 'md' ? 13 : 11;

	if (level === 'pending') {
		return (
			<span className={cn('inline-flex', scale)} aria-hidden>
				<Clock3
					size={clockSize}
					strokeWidth={2.5}
					className={cn('shrink-0 text-stone-400', className)}
				/>
			</span>
		);
	}

	if (level === 'failed') {
		return (
			<span className={cn('inline-flex', scale)} aria-hidden>
				<TriangleAlert
					size={clockSize}
					strokeWidth={2.5}
					className={cn('shrink-0 text-rose-500', className)}
				/>
			</span>
		);
	}

	if (level === 'cancelled') {
		return (
			<span className={cn('inline-flex', scale)} aria-hidden>
				<Ban
					size={clockSize}
					strokeWidth={2.5}
					className={cn('shrink-0 text-zinc-400', className)}
				/>
			</span>
		);
	}

	const iconClass = cn(RECEIPT_CLASS[level], className);

	if (level === 'sent') {
		return (
			<span className={cn('inline-flex', scale)} aria-hidden>
				<WhatsAppSingleCheck className={iconClass} />
			</span>
		);
	}

	return (
		<span className={cn('inline-flex', scale)} aria-hidden>
			<WhatsAppDoubleCheck className={iconClass} />
		</span>
	);
};

export { WhatsAppMessageReceipt };
